import { useState } from 'react';
import { useAgentApi } from '../Services/AgentApiProvider';
import { AssistantInstructionApi } from '../Services/AgentApiProviderTypes';
import { useAlert } from './AlertContext';
import Button from './Buttons';
import { CustomInput, CustomTextarea, CustomToggle } from './CustomFormElements';
import PageLoader from './PageLoader';

function AgentInstructionEdit({
  agentId,
  canEdit,
  initialData,
  onClose,
  onUpdate,
  onUpdateTrigger,
}: {
  agentId: string;
  canEdit: boolean;
  initialData: AssistantInstructionApi;
  onClose: () => void;
  onUpdate: () => void;
  onUpdateTrigger: () => void;
}) {
  const { showAlert } = useAlert();
  const { createAgentInstruction, updateAgentInstruction } = useAgentApi();

  const isNew = !initialData.id;
  const [loading, setLoading] = useState<boolean>(false);

  const [instruction, setInstruction] = useState<AssistantInstructionApi>(initialData);

  const handleSave = async (): Promise<void> => {
    try {
      if (!instruction.original_text.trim()) {
        showAlert('fail', 'Original text is required');
        return;
      }

      if (!instruction.instruction.trim()) {
        showAlert('fail', 'Instruction is required');
        return;
      }

      setLoading(true);

      if (isNew) {
        await createAgentInstruction(agentId, {
          original_text: instruction.original_text,
          instruction: instruction.instruction,
        });
      } else {
        await updateAgentInstruction(agentId, initialData.id, {
          original_text: instruction.original_text,
          instruction: instruction.instruction,
          is_active: instruction.is_active,
        });
      }
      onUpdate();
      onUpdateTrigger();
    } catch (error) {
      showAlert('fail', 'Failed to save instruction.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="d-flex flex-column h-100">
      <h2 className="h4-title">{isNew ? 'Create New Instruction' : 'Edit Instruction'}</h2>

      <div style={{ flexGrow: 1, paddingTop: '1rem' }}>
        <div className="input-block d-flex flex-row justify-between">
          <div className="input-description">
            <div className="input-title">Original Text</div>
            <div className="small-font">Enter original text, or query</div>
          </div>
          <div className="align-wrapper d-flex align-center">
            <CustomInput
              id="original-text"
              label="Original Text"
              placeholder="Enter original text or query"
              value={instruction.original_text}
              onChange={(_, value) => setInstruction({ ...instruction, original_text: value })}
              disabled={!canEdit}
            />
          </div>
        </div>
        <div className="input-block d-flex flex-row justify-between">
          <div className="input-description">
            <div className="input-title">Instruction</div>
            <div className="small-font">Add instruction for Agent</div>
          </div>
          <div className="align-wrapper d-flex align-center">
            <CustomTextarea
              id="instruction"
              label="Instruction"
              name="instruction"
              addedText={instruction.instruction}
              onChange={(value) => setInstruction({ ...instruction, instruction: value })}
              resizable={false}
              disabled={!canEdit}
              height="200px"
            />
          </div>
        </div>
        <div className="input-block d-flex flex-row justify-between">
          <div className="input-description">
            <div className="input-title">Activate Instruction</div>
            <div className="small-font">
              Set agent instruction to be active or inactive to enable or disable it
            </div>
          </div>
          <div className="align-wrapper d-flex align-center">
            <CustomToggle
              id="toggle-status"
              label={instruction.is_active ? 'Active' : 'Inactive'}
              checked={instruction.is_active}
              onChange={(_, value) => setInstruction({ ...instruction, is_active: value })}
              disabled={!canEdit}
            />
          </div>
        </div>
      </div>

      <div className="buttons-container d-flex justify-end">
        {canEdit && (
          <Button variant="primary" onClick={handleSave}>
            {isNew ? 'Create' : 'Save Changes'}
          </Button>
        )}
        <Button variant="danger" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export default AgentInstructionEdit;
